import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Modal } from "@/components/Modals/Modal";
import { Spinner } from "@/components/Spinner/Spinner";

import { useGetCsvEnrichmentStatisticsQuery } from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { classNames } from "@/utils/classNames";

import { buildStatisticsModalGroups, StatisticsModalRow } from "./xlsStatisticsGroups";

const TONE_CLASS: Record<StatisticsModalRow["tone"], string> = {
	neutral: "text-text-meta",
	ok: "text-status-success",
	warn: "text-status-running",
	bad: "text-status-error",
};

interface XlsStatisticsModalProps {
	csvEnrichmentId: number;
	sourceName?: string;
	handleClose: () => void;
}

/**
 * PREC-3870 — what one uploaded XLSX source produced, opened from the Added sources table after
 * the file has been enriched. The lead buckets and their reason children are the list's own
 * grouped Show counts scoped to this source, so the modal and the list can never disagree.
 */
export const XlsStatisticsModal: FunctionComponent<XlsStatisticsModalProps> = ({
	csvEnrichmentId,
	sourceName,
	handleClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`csvEnrichment.statsModal.${key}`);
	const tsInbox = (key: string) => t(`inbox.${key}`);

	const { data: statistics, isLoading } = useGetCsvEnrichmentStatisticsQuery({
		id: csvEnrichmentId,
	});

	const groups = buildStatisticsModalGroups(ts, tsInbox, statistics);

	return (
		<Modal
			cancelButtonText={ts("close")}
			handleClose={handleClose}
			isOpened={true}
			size="sm"
			subtitle={`${sourceName ?? statistics?.fileName ?? ""} · ${ts("subtitle")}`}
			title={ts("title")}
		>
			<div className="flex flex-col" data-test="xls-statistics-modal">
				{isLoading && (
					<div className="flex justify-center py-8">
						<Spinner />
					</div>
				)}

				{!isLoading &&
					groups.map((group) => (
						<div key={group.key}>
							<div className="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-text-meta">
								{group.title}
							</div>
							{group.rows.map((row) => (
								<StatisticsRow key={row.key} row={row} />
							))}
						</div>
					))}

				{!isLoading && !groups.length && (
					<p className="py-8 text-center text-sm text-text-meta">{ts("empty")}</p>
				)}
			</div>
		</Modal>
	);
};

/** One figure, with its reason breakdown folded away behind a chevron when it has one. */
const StatisticsRow: FunctionComponent<{ row: StatisticsModalRow }> = ({ row }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const hasReasons = !!row.reasons?.length;

	return (
		<>
			<div
				className={classNames(
					"flex flex-row items-center gap-3 border-b border-gray-100 px-4 py-2.5 text-sm",
					hasReasons && "cursor-pointer hover:bg-gray-50"
				)}
				data-test={`xls-statistics-modal-${row.key}`}
				onClick={hasReasons ? () => setIsExpanded(!isExpanded) : undefined}
			>
				<span className={classNames("flex-1", TONE_CLASS[row.tone])}>{row.label}</span>
				{hasReasons && (
					<Icon
						className={classNames("h-3 w-3 text-text-meta", isExpanded && "rotate-180")}
						icon={faChevronDown}
					/>
				)}
				<span className="font-semibold text-text">{row.value}</span>
			</div>

			{hasReasons &&
				isExpanded &&
				row.reasons?.map((reason) => (
					<div
						key={reason.filterParam}
						className="flex flex-row items-center gap-3 border-b border-gray-100 bg-gray-50 py-1.5 pl-10 pr-4 text-xs"
						data-test={`xls-statistics-modal-reason-${reason.filterParam}`}
					>
						<span className="flex-1 text-text-meta">{reason.label}</span>
						<span className="font-medium text-text">{reason.count}</span>
					</div>
				))}
		</>
	);
};
